import { useJSONFetcher } from "@outplayed/json-fetcher";
import SubtitleAndDescription from "components/Common/SubtitleAndDescription";
import React from "react";
import { useGlobal } from "reactn";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import { getEncounterId, useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getDifficultyPath,
  getFormattedContentType,
  getFormattedEncounterType,
  getFormattedNames,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  PATCH,
} from "../../../lib/wowHelpers";
import NoData from "../../Common/NoData";
import PvEGearGuide from "../../GearGuide/PvEGearGuide";
import TierSetContainer from "../Builds/TierSetContainer";
import Layout from "../Layout";
import GearContainer, { getPrimaryMetric, ItemsSubContainerCategory } from "./GearContainer";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { ReactComponent as AlertIcon } from "svg/alert-fill.svg";
import { ReactComponent as ExternalLinkIcon } from "svg/external-link-line.svg";
import { ReactComponent as IcyVeinsLogo } from "svg/icy_veins_logo.svg";
import { ReactComponent as ChevronIcon } from "svg/chevron-back.svg";
import MediaQuery from "components/MediaQuery";

const GearPageContainer = () => {
  const [windowBreakpoint] = useGlobal("responsive");
  const isMobile =
    windowBreakpoint === "MOBILE_SMALL" || windowBreakpoint === "MOBILE_MEDIUM" || windowBreakpoint === "MOBILE_LARGE";

  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  const { difficulty, affix } = useQueryString();
  const diffPath = getDifficultyPath(difficulty, encounterType);
  const { data: gearTable, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${diffPath}/${dataClassName}/${dataClassName}_itemsTable.json`,
    { ssr: true },
  );

  if (isLoading) {
    return null;
  }

  const dataClassSpec = `${dataClassName}-${dataSpecName}`;
  const hasData =
    gearTable &&
    (affix && affix !== "all"
      ? (gearTable.affixes?.[affix]?.[encounterID]?.[dataClassSpec]?.items_table?.items?.belt?.total_count ?? 0) > 0
      : (gearTable[encounterType]?.[encounterID]?.[dataClassSpec]?.items_table?.items?.belt?.total_count ?? 0) > 0);

  const classSpecGear =
    hasData &&
    (affix && affix !== "all"
      ? gearTable.affixes[affix][encounterID][dataClassSpec].items_table
      : gearTable[encounterType][encounterID][dataClassSpec].items_table);
  const bisGear =
    hasData &&
    (affix && affix !== "all"
      ? gearTable.affixes[affix][encounterID][dataClassSpec]
      : gearTable[encounterType][encounterID][dataClassSpec]);

  const specClass = `${displaySpecName} ${displayClassName}`;
  const specClass2 = `${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(displayClassName)}`;
  const contentType = getFormattedContentType(encounterType);
  const ivGearGuideURL = `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
    classType,
  )}-pve-${getIcyVeinsRole(specType)}-gear-best-in-slot`;
  const pageDescription = `Data-driven ${displaySpecName} ${displayClassName} gear from top players. The top ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} gear for ${getFormattedEncounterType(encounterType)} in ${EXPANSION} ${PATCH}.`;

  const EmbellishmentsAlert = () => (
    <div className="embellishments-alert">
      <div className="left-side">
        <AlertIcon className="alert-icon" />{" "}
        <div className="text-container">
          Due to technical limitations, we can't provide data for all embellishments. Check out{" "}
          <a href={ivGearGuideURL} target="_blank">
            <ExternalLinkIcon className="external-link-icon" /> <strong>Icy Veins</strong>
          </a>{" "}
          for the best choices!
        </div>
      </div>
      <a href={ivGearGuideURL} target="_blank">
        <div className="right-side">
          <IcyVeinsLogo className="icy-veins-logo" />
          <ChevronIcon className="chevron-icon" />
        </div>
      </a>
    </div>
  );

  return (
    <Layout
      className="wow-gear-page-container"
      classType={classType}
      filterPage="builds"
      metaTitle={`${specClass} Gear ${EXPANSION} ${PATCH}`}
      pageDescription={pageDescription}
      pageTitle={`Gear for ${getFormattedEncounterType(encounterType)}`}
      specType={specType}
    >
      {hasData ? (
        <>
          <PvEGearGuide
            builds={bisGear.items}
            combos={bisGear.combos}
            classType={classType}
            specType={specType}
            encounterType={encounterType}
            encounter={encounterID}
          />
          <TierSetContainer className={displayClassName} specName={displaySpecName} encounterType={encounterType} />
          <div className="wow-items-page embellishments-container">
            <MediaQuery min="MOBILE_SMALL" max="TABLET" renderNullOnFail>
              <a href={ivGearGuideURL} target="_blank">
                <EmbellishmentsAlert />
              </a>
            </MediaQuery>
            <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
              <EmbellishmentsAlert />
            </MediaQuery>
            <ItemsSubContainerCategory
              category="Embellishment Items"
              metricOneName={getPrimaryMetric(displaySpecName, encounterType)}
              metricTwoName="Popularity"
              data={classSpecGear.embellishments}
              count={classSpecGear.embellishments.reduce((acc, x) => acc + x.count, 0)}
            />
          </div>
          <div className="seperator" />
          <SubtitleAndDescription
            subtitle={`${specClass2} Gear Tables`}
            description={`These tables are a comparison of the most popular items in each gear slot for ${contentType}.`}
          />
          <GearContainer
            classSpecGear={classSpecGear}
            isMobile={isMobile}
            displaySpecName={displaySpecName}
            encounterType={encounterType}
          />
          <PageSectionCrossLinks
            classType={classType}
            description={`Our friends at Icy Veins have you covered, with an in-depth guide to suit up your ${displaySpecName} ${displayClassName} with the best gear.`}
            guideData={[
              {
                description: `Find the best in slot gear for ${displaySpecName} ${displayClassName}`,
                link: ivGearGuideURL,
                title: "Gear and Best in Slot Guide",
              },
            ]}
            specType={specType}
          />
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default GearPageContainer;
